import React from 'react';
import PropTypes from 'prop-types';
import { formatGameDateTime } from '../../utils/timeUtils';
import { formatLine } from '../../utils/lineDataUtils';
import { formatBettingLine, formatGamePeriod, formatLineMovement } from '../../utils/gameDetailUtils';
import InfoTip from './InfoTip';
import styles from './GameDetailModal.module.css';

const SportsFootballIcon = () => (
  <svg className={styles.possessionIcon} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16">
    <path d="M3.02 15.62c-.08 2.42.32 4.34.67 4.69s2.28.76 4.69.67l-5.36-5.36zM13.08 3.28c-2.33.42-4.79 1.34-6.62 3.18s-2.76 4.29-3.18 6.62l7.63 7.63c2.34-.41 4.79-1.34 6.62-3.18s2.76-4.29 3.18-6.62l-7.63-7.63zM9.9 15.5l-1.4-1.4 5.6-5.6 1.4 1.4-5.6 5.6zm11.08-7.12c.08-2.42-.32-4.34-.67-4.69s-2.28-.76-4.69-.67l5.36 5.36z" />
  </svg>
);

const formatElo = (value) => (value != null ? Math.round(value) : null);

const formatCoreValue = (value) => {
  if (value == null) return null;
  const rounded = Number(value).toFixed(1);
  return value > 0 ? `+${rounded}` : rounded;
};

const getCoreForTeam = (matchupStats, teamId) => {
  if (!matchupStats || teamId == null) return null;
  if (matchupStats.awayTeam?.teamId === teamId) return matchupStats.awayTeam.core;
  if (matchupStats.homeTeam?.teamId === teamId) return matchupStats.homeTeam.core;
  return null;
};

const LineMovementNote = ({ openLine, currentLine, isSpread = true }) => {
  const movement = formatLineMovement(openLine, currentLine);
  if (openLine == null || movement === null) return null;
  if (movement === 'unchanged') {
    return <span className={styles.lineMovementNeutral}>Open {formatBettingLine(openLine)}</span>;
  }
  const arrow = movement === 'up' ? '↗' : '↘';
  return (
    <span className={styles.lineMovement}>
      Open {formatBettingLine(openLine)} {arrow}
    </span>
  );
};

LineMovementNote.propTypes = {
  openLine: PropTypes.number,
  currentLine: PropTypes.number,
  isSpread: PropTypes.bool,
};

const GameDetailOverview = ({ game, matchupStats }) => {
  const { displayDate, displayTime } = formatGameDateTime(game.start_date);
  const venue = game.venue || {};
  const weather = game.weather || {};
  const betting = game.betting || {};

  return (
    <div className={styles.section}>
      <div className={styles.metaRow}>
        {displayDate} • {displayTime}
        {game.tv && ` • ${game.tv}`}
      </div>

      {venue.name && (
        <div className={styles.infoRow}>
          <span className={styles.infoIcon} aria-hidden>🏟️</span>
          <span>
            {venue.name}
            {venue.city && venue.state && ` • ${venue.city}, ${venue.state}`}
            {game.neutral_site && <span className={styles.neutralSite}> • Neutral Site</span>}
          </span>
        </div>
      )}

      {game.game_status === 'in_progress' && (
        <div className={styles.liveStrip}>
          <div className={styles.liveIndicator}>
            <span className={styles.liveDot} aria-hidden>●</span>
            LIVE • {formatGamePeriod(game.clock, game.period)}
          </div>
          {game.situation && <div className={styles.situation}>{game.situation}</div>}
          {game.possession && (
            <div className={styles.possession}>
              Possession:{' '}
              {game.possession === 'home' ? game.home_team.name : game.away_team.name}
              <SportsFootballIcon />
            </div>
          )}
          {game.last_play && <div className={styles.lastPlay}>{game.last_play}</div>}
        </div>
      )}

      {weather.condition && (
        <div className={styles.infoRow}>
          <span className={styles.infoIcon} aria-hidden>🌤️</span>
          <span>
            {weather.condition}
            {weather.temperature != null && ` • ${weather.temperature}°F`}
            {weather.wind_speed != null && ` • Wind: ${weather.wind_speed} mph`}
            {weather.game_indoors && <span className={styles.indoorGame}> • Indoor</span>}
          </span>
        </div>
      )}

      <div className={styles.metricsRow}>
        <div className={styles.metricInline}>
          <span className={styles.metricLabel}>Spread</span>
          <span className={styles.metricValue}>{formatBettingLine(betting.spread)}</span>
          <LineMovementNote openLine={betting.spread_open} currentLine={betting.spread} />
        </div>
        <div className={styles.metricInline}>
          <span className={styles.metricLabel}>Total</span>
          <span className={styles.metricValue}>{formatBettingLine(betting.over_under)}</span>
          <LineMovementNote openLine={betting.over_under_open} currentLine={betting.over_under} isSpread={false} />
        </div>
        {game.excitement_index != null && (
          <div className={styles.metricInline}>
            <span className={styles.metricLabel}>Excitement</span>
            <span className={styles.metricValue}>{game.excitement_index.toFixed(1)}</span>
          </div>
        )}
      </div>

      {(betting.home_moneyline != null || betting.away_moneyline != null) && (
        <div className={styles.moneylineRow}>
          ML: {game.away_team.abbreviation || game.away_team.name}{' '}
          {betting.away_moneyline != null ? formatLine(betting.away_moneyline) : 'N/A'}
          {'  |  '}
          {game.home_team.abbreviation || game.home_team.name}{' '}
          {betting.home_moneyline != null ? formatLine(betting.home_moneyline) : 'N/A'}
        </div>
      )}

      <div className={styles.teamBlock}>
        <h3 className={styles.sectionTitle}>Teams</h3>
        {[game.away_team, game.home_team].map((team, index) => {
          const isAway = index === 0;
          const score = isAway ? game.away_score : game.home_score;
          const pregameElo = isAway ? game.away_pregame_elo : game.home_pregame_elo;
          const core = getCoreForTeam(matchupStats, team.id);
          return (
            <div key={team.id || team.name} className={styles.teamRow}>
              <div className={styles.teamInfo}>
                <div className={styles.teamNameRow}>
                  {team.rank && <span className={styles.rankBadge}>#{team.rank}</span>}
                  {team.logos?.[0] && (
                    <img src={team.logos[0]} alt="" className={styles.teamLogo} />
                  )}
                  <span>{team.name}</span>
                </div>
                <div className={styles.teamMeta}>
                  {team.record && <span>{team.record}</span>}
                  {team.conference && <span>{team.conference}</span>}
                  {formatElo(pregameElo) != null && (
                    <span className={styles.eloMeta}>
                      ELO {formatElo(pregameElo)}
                      <InfoTip
                        label="What is ELO?"
                        text="ELO ratings estimate team strength from historical results. Higher ELO means a stronger team heading into the game."
                      />
                    </span>
                  )}
                  {core && (
                    <span className={styles.coreMeta}>
                      CORE O {formatCoreValue(core.offense)} • D {formatCoreValue(core.defense)}
                      {core.overall != null && ` • ${formatCoreValue(core.overall)}`}
                      <InfoTip
                        label="What is CORE?"
                        text="CORE (Context & Opponent-Relative Efficiency) measures how much value a team creates on offense and allows on defense, adjusted for game situation and opponent strength. Higher offense and lower defense values are better."
                      />
                    </span>
                  )}
                </div>
              </div>
              {game.game_status !== 'scheduled' && (
                <div className={styles.scoreDisplay}>{score ?? 0}</div>
              )}
            </div>
          );
        })}
      </div>

      <div className={styles.badgeRow}>
        {Boolean(game.conference_game) && (
          <span className={styles.badgeConference}>Conference Game</span>
        )}
        {Boolean(game.neutral_site) && (
          <span className={styles.badgeNeutral}>Neutral Site</span>
        )}
        {game.attendance > 0 && (
          <span className={styles.badgeAttendance}>
            Attendance: {game.attendance.toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
};

GameDetailOverview.propTypes = {
  game: PropTypes.object.isRequired,
  matchupStats: PropTypes.object,
};

export default GameDetailOverview;
